import { FilterFieldGroup } from '@App/forms/common/filter-field-group'
import { FormFieldTypes } from '@App/enum/form-field-types'

/** How a filter field's value is encoded in a query string: repeated keys, 'true', or a single string. */
export type FilterFieldKind = 'array' | 'boolean' | 'scalar'

export interface FilterFieldMeta {
    name: string
    kind: FilterFieldKind
}

const kindOf = (type: FormFieldTypes): FilterFieldKind => {
    if (type === FormFieldTypes.CHECKBOX_GROUP || type === FormFieldTypes.MULTI_DROPDOWN) {
        return 'array'
    }

    return type === FormFieldTypes.CHECKBOX ? 'boolean' : 'scalar'
}

/** Flattens the sidebar's filter groups into the list of fields that can be carried in a URL. */
export const getFilterFieldMeta = <K extends object>(
    filterFieldCollections: Array<FilterFieldGroup<K>> | undefined
): FilterFieldMeta[] =>
    (filterFieldCollections ?? []).flatMap((group) =>
        group.filterFields.flatMap((fieldRecord) =>
            Object.values(fieldRecord).map((field) => ({ name: field.name as string, kind: kindOf(field.type) }))
        )
    )

const isBlank = (value: unknown) => value === undefined || value === null || value === '' || value === false

/** Reads the known filter fields out of a query string; undefined when none are present. */
export const parseFiltersFromSearch = (
    search: string,
    fields: FilterFieldMeta[]
): Record<string, unknown> | undefined => {
    const params = new URLSearchParams(search)
    const result: Record<string, unknown> = {}

    fields.forEach(({ name, kind }) => {
        if (!params.has(name)) {
            return
        }
        if (kind === 'array') {
            result[name] = params.getAll(name)
        } else if (kind === 'boolean') {
            result[name] = params.get(name) === 'true'
        } else {
            result[name] = params.get(name) ?? undefined
        }
    })

    return Object.keys(result).length > 0 ? result : undefined
}

/**
 * Returns `currentSearch` with the known filter fields replaced by `filters` (blank values omitted),
 * leaving unrelated params untouched. The result includes the leading "?" or is empty.
 */
export const serializeFiltersToSearch = (
    currentSearch: string,
    filters: Record<string, unknown> | undefined,
    fields: FilterFieldMeta[]
): string => {
    const params = new URLSearchParams(currentSearch)
    fields.forEach(({ name }) => params.delete(name))

    fields.forEach(({ name, kind }) => {
        const value = filters?.[name]
        if (isBlank(value)) {
            return
        }
        if (kind !== 'array') {
            params.set(name, String(value))
        } else if (Array.isArray(value)) {
            value.filter((entry) => !isBlank(entry)).forEach((entry) => params.append(name, String(entry)))
        }
    })

    const search = params.toString()

    return search ? `?${search}` : ''
}
